import { createReducer, on } from '@ngrx/store';
import { AvatarCreateActions } from './create.actions';
import {
  AvatarCreateStatus,
  AvatarCreateStep,
  avatarCreateState
} from './create.state';

/**
 * Reducer for avatar create feature.
 *
 * @exports
 */
export const avatarCreateReducer = createReducer(
  avatarCreateState,

  // Initialize flow
  on(AvatarCreateActions.initializeFlow, () => ({
    ...avatarCreateState,
    currentStep: AvatarCreateStep.PHOTO_SELECTION
  })),

  // Open camera
  on(AvatarCreateActions.openCamera, (state) => ({
    ...state,
    loading: { ...state.loading, takingPhoto: true },
    error: null
  })),

  // Open gallery
  on(AvatarCreateActions.openGallery, (state) => ({
    ...state,
    loading: { ...state.loading, selectingFromGallery: true },
    error: null
  })),

  // Photo selected
  on(AvatarCreateActions.photoSelected, (state, { dataUrl, sourceType }) => ({
    ...state,
    selectedImage: {
      dataUrl,
      sourceType
    },
    currentStep: AvatarCreateStep.CONFIRMATION,
    validationError: null,
    loading: {
      ...state.loading,
      takingPhoto: false,
      selectingFromGallery: false
    },
    error: null
  })),

  // Photo selection failed
  on(AvatarCreateActions.photoSelectionFailed, (state, { error }) => ({
    ...state,
    error,
    loading: {
      ...state.loading,
      takingPhoto: false,
      selectingFromGallery: false
    }
  })),

  // Retake picture
  on(AvatarCreateActions.retakePicture, (state) => ({
    ...state,
    currentStep: AvatarCreateStep.PHOTO_SELECTION,
    selectedImage: {
      dataUrl: null,
      sourceType: null
    },
    validationError: null,
    error: null
  })),

  // Upload avatar
  on(AvatarCreateActions.uploadAvatar, (state) => ({
    ...state,
    currentStep: AvatarCreateStep.LOADING,
    loading: { ...state.loading, uploading: true },
    status: AvatarCreateStatus.UPLOADING,
    error: null,
    validationError: null
  })),

  // Upload avatar success
  on(AvatarCreateActions.uploadAvatarSuccess, (state, { avatarId }) => ({
    ...state,
    avatarId,
    currentStep: AvatarCreateStep.LOADING,
    status: AvatarCreateStatus.PROCESSING,
    loading: { ...state.loading, uploading: false },
    error: null
  })),

  // Upload avatar failed
  on(AvatarCreateActions.uploadAvatarFailed, (state, { error }) => ({
    ...state,
    currentStep: AvatarCreateStep.CONFIRMATION,
    error,
    status: AvatarCreateStatus.FAILED,
    loading: { ...state.loading, uploading: false }
  })),

  // Validation error
  on(AvatarCreateActions.validationError, (state, { error }) => ({
    ...state,
    currentStep: AvatarCreateStep.CONFIRMATION,
    validationError: error,
    loading: { ...state.loading, uploading: false },
    status: AvatarCreateStatus.IDLE
  })),

  // Start polling
  on(AvatarCreateActions.startPolling, (state) => ({
    ...state,
    loading: { ...state.loading, polling: true },
    pollingAttempts: 0,
    pollingStartTime: Date.now()
  })),

  // Poll status
  on(AvatarCreateActions.pollStatus, (state) => ({
    ...state,
    loading: { ...state.loading, polling: true }
  })),

  // Poll status success
  on(AvatarCreateActions.pollStatusSuccess, (state) => ({
    ...state,
    pollingAttempts: state.pollingAttempts + 1,
    loading: { ...state.loading, polling: false }
  })),

  // Poll status failed
  on(AvatarCreateActions.pollStatusFailed, (state, { error }) => ({
    ...state,
    error,
    loading: { ...state.loading, polling: false }
  })),

  // Stop polling
  on(AvatarCreateActions.stopPolling, (state) => ({
    ...state,
    loading: { ...state.loading, polling: false },
    pollingAttempts: 0,
    pollingStartTime: null
  })),

  // Polling timeout (10 seconds)
  on(AvatarCreateActions.pollingTimeout, (state) => ({
    ...state,
    status: AvatarCreateStatus.PROCESSING_BACKGROUND,
    loading: { ...state.loading, polling: false },
    pollingAttempts: 0,
    pollingStartTime: null
  })),

  // Polling max attempts reached
  on(AvatarCreateActions.pollingMaxAttemptsReached, (state) => ({
    ...state,
    status: AvatarCreateStatus.PROCESSING_BACKGROUND,
    loading: { ...state.loading, polling: false },
    pollingAttempts: 0,
    pollingStartTime: null
  })),

  // Avatar completed
  on(AvatarCreateActions.avatarCompleted, (state, { avatarId }) => ({
    ...state,
    avatarId,
    status: AvatarCreateStatus.COMPLETED,
    loading: { ...state.loading, polling: false },
    pollingAttempts: 0,
    pollingStartTime: null
  })),

  // Avatar failed
  on(AvatarCreateActions.avatarFailed, (state, { error }) => ({
    ...state,
    error,
    status: AvatarCreateStatus.FAILED,
    loading: { ...state.loading, polling: false },
    pollingAttempts: 0,
    pollingStartTime: null
  })),

  // Set step
  on(AvatarCreateActions.setStep, (state, { step }) => ({
    ...state,
    currentStep: step as AvatarCreateStep
  })),

  // Reset state
  on(AvatarCreateActions.resetState, () => ({
    ...avatarCreateState
  }))
);
